import { Check, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  CustomOrgRolePermission,
  OrgRoleView,
  TenantOrgPermission,
} from '@nadoutong/contracts';
import { ORG_ROLE_NAME_MAX_LENGTH, isCustomOrgRolePermission } from '../../api/roles.ts';
import { useCreateOrgRole, useDeleteOrgRole, useUpdateOrgRole } from '../../api/useRoles.ts';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, ConfirmDialog, Input, Panel } from '../../ui/index.ts';
import { builtinRoleLabelKey, isBuiltinOrgRoleId } from './orgRoleLabels.ts';
import { ORG_ROLE_PERMISSION_GROUPS } from './orgRolePermissions.ts';

/**
 * Role matrix — `/organization?tab=permissions` (tenant #178). Model
 * permissions live on the sibling `model_permissions` tab.
 *
 * Left: builtin roles (owner/admin/member/finance) locked; custom
 * roles with create / rename / delete. Right: grouped `org:*`
 * checklist. Builtins are
 * read-only; custom roles save through PATCH. When `GET /v1/org/roles`
 * 404s or errors, the page renders the local builtin fallback and
 * disables custom-role editing.
 */

interface Props {
  readonly orgId: string | undefined;
  readonly roles: readonly OrgRoleView[];
  readonly degraded: boolean;
  readonly canManage: boolean;
}

export function RolesPanel({ orgId, roles, degraded, canManage }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createM = useCreateOrgRole(orgId);
  const updateM = useUpdateOrgRole(orgId);
  const deleteM = useDeleteOrgRole(orgId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<readonly CustomOrgRolePermission[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgRoleView | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const selected = useMemo(
    () => roles.find((role) => role.id === selectedId) ?? roles[0] ?? null,
    [roles, selectedId],
  );
  const canEditCustom = canManage && !degraded && orgId !== undefined;
  const mutationPending = createM.isPending || updateM.isPending || deleteM.isPending;
  const checklistReadOnly = selected === null || selected.builtin || !canEditCustom;

  useEffect(() => {
    if (selected === null) {
      return;
    }
    if (selectedId === null || !roles.some((role) => role.id === selectedId)) {
      setSelectedId(selected.id);
    }
    setDraftPermissions(customPermissionsOf(selected.permissions));
    setSaveError(null);
  }, [roles, selected, selectedId]);

  async function handleCreate(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canEditCustom) {
      return;
    }
    setCreateError(null);
    const name = createName.trim();
    if (name.length === 0) {
      setCreateError(t('organization.roles.name_required'));
      return;
    }
    try {
      const created = await createM.mutateAsync({ name, permissions: [] });
      setCreateName('');
      setSelectedId(created.role.id);
      toast.success(t('organization.roles.toast.created'));
    } catch (err) {
      setCreateError(localizeCaughtError(err, t, 'organization.roles.error.create'));
    }
  }

  async function handleRename(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canEditCustom || editingRoleId === null) {
      return;
    }
    setEditError(null);
    const name = editName.trim();
    if (name.length === 0) {
      setEditError(t('organization.roles.name_required'));
      return;
    }
    try {
      await updateM.mutateAsync({ roleId: editingRoleId, body: { name } });
      setEditingRoleId(null);
      toast.success(t('organization.roles.toast.renamed'));
    } catch (err) {
      setEditError(localizeCaughtError(err, t, 'organization.roles.error.rename'));
    }
  }

  async function handleSavePermissions(): Promise<void> {
    if (!canEditCustom || selected === null || selected.builtin) {
      return;
    }
    setSaveError(null);
    try {
      await updateM.mutateAsync({
        roleId: selected.id,
        body: { permissions: draftPermissions },
      });
      toast.success(t('organization.roles.toast.updated'));
    } catch (err) {
      setSaveError(localizeCaughtError(err, t, 'organization.roles.error.save'));
    }
  }

  async function handleDelete(): Promise<void> {
    if (!canEditCustom || deleteTarget === null) {
      return;
    }
    setDeleteError(null);
    try {
      await deleteM.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      if (selectedId === deleteTarget.id) {
        setSelectedId(null);
      }
      toast.success(t('organization.roles.toast.deleted'));
    } catch (err) {
      setDeleteError(localizeCaughtError(err, t, 'organization.roles.error.delete'));
    }
  }

  function togglePermission(id: CustomOrgRolePermission): void {
    if (checklistReadOnly) {
      return;
    }
    setDraftPermissions((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
    setSaveError(null);
  }

  function selectRole(roleId: string): void {
    setSelectedId(roleId);
  }

  return (
    <div data-testid="organization-roles" className="flex flex-col gap-4">
      {degraded ? (
        <p
          data-testid="organization-roles-degraded"
          className="rounded-lg border border-line bg-card px-4 py-3 text-body text-ink-low"
        >
          {t('organization.roles.degraded')}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]">
        <Panel
          caption={t('organization.roles.title')}
          count={roles.length}
          testId="organization-roles-list"
        >
          <p className="m-0 border-b border-line-soft px-4 py-3 text-caption text-ink-low">
            {t('organization.roles.subtitle')}
          </p>
          <ul className="m-0 flex list-none flex-col p-0">
            {roles.map((role) => {
              const active = selected?.id === role.id;
              const editing = editingRoleId === role.id;
              return (
                <li
                  key={role.id}
                  data-testid={`organization-roles-row-${role.id}`}
                  className={[
                    'border-b border-line-soft last:border-b-0',
                    active ? 'bg-active/40' : '',
                  ].join(' ')}
                >
                  {editing ? (
                    <form
                      className="flex items-center gap-2 px-4 py-2"
                      onSubmit={(event) => {
                        void handleRename(event);
                      }}
                    >
                      <Input
                        value={editName}
                        maxLength={ORG_ROLE_NAME_MAX_LENGTH}
                        invalid={editError !== null}
                        onChange={(event) => {
                          setEditName(event.currentTarget.value);
                          setEditError(null);
                        }}
                        aria-label={t('organization.roles.create_name')}
                        data-testid={`organization-roles-rename-input-${role.id}`}
                      />
                      <button
                        type="submit"
                        disabled={mutationPending}
                        title={t('organization.action.save')}
                        aria-label={t('organization.roles.rename', { name: role.name })}
                        data-testid={`organization-roles-rename-save-${role.id}`}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-forest hover:bg-active disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <Check aria-hidden="true" className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={mutationPending}
                        onClick={() => {
                          setEditingRoleId(null);
                          setEditError(null);
                        }}
                        title={t('organization.action.cancel')}
                        aria-label={t('organization.action.cancel')}
                        data-testid={`organization-roles-rename-cancel-${role.id}`}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-low hover:bg-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <X aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => selectRole(role.id)}
                        data-testid={`organization-roles-select-${role.id}`}
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:text-forest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-body font-medium text-ink">
                            {isBuiltinOrgRoleId(role.id)
                              ? t(builtinRoleLabelKey(role.id))
                              : role.name}
                          </span>
                          <span className="block font-mono text-caption text-ink-low">
                            {t('organization.roles.members_count', { count: role.memberCount })}
                          </span>
                        </span>
                        {role.builtin ? (
                          <Chip
                            tone="neutral"
                            lamp={false}
                            testId={`organization-roles-builtin-${role.id}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              <Lock aria-hidden="true" className="h-3 w-3" />
                              {t('organization.roles.builtin')}
                            </span>
                          </Chip>
                        ) : (
                          <Chip
                            tone="rust"
                            lamp={false}
                            testId={`organization-roles-custom-${role.id}`}
                          >
                            {t('organization.roles.custom')}
                          </Chip>
                        )}
                      </button>
                      {canEditCustom && !role.builtin ? (
                        <span className="flex shrink-0 items-center">
                          <button
                            type="button"
                            disabled={mutationPending}
                            onClick={() => {
                              setEditingRoleId(role.id);
                              setEditName(role.name);
                              setEditError(null);
                            }}
                            title={t('organization.action.edit')}
                            aria-label={t('organization.roles.rename', { name: role.name })}
                            data-testid={`organization-roles-rename-${role.id}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-low hover:bg-active hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                          >
                            <Pencil aria-hidden="true" className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={mutationPending}
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget(role);
                            }}
                            title={t('organization.roles.delete', { name: role.name })}
                            aria-label={t('organization.roles.delete', { name: role.name })}
                            data-testid={`organization-roles-delete-${role.id}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-bad hover:bg-bad-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
                          >
                            <Trash2 aria-hidden="true" className="h-4 w-4" />
                          </button>
                        </span>
                      ) : null}
                    </div>
                  )}
                  {editing && editError !== null ? (
                    <p
                      role="alert"
                      data-testid="organization-roles-rename-error"
                      className="m-0 px-4 pb-2 text-caption text-bad"
                    >
                      {editError}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {canEditCustom ? (
            <form
              className="flex flex-col gap-2 border-t border-line-soft px-4 py-3"
              onSubmit={(event) => {
                void handleCreate(event);
              }}
            >
              <Input
                value={createName}
                maxLength={ORG_ROLE_NAME_MAX_LENGTH}
                invalid={createError !== null}
                placeholder={t('organization.roles.create_name_placeholder')}
                aria-label={t('organization.roles.create_name')}
                onChange={(event) => {
                  setCreateName(event.currentTarget.value);
                  setCreateError(null);
                }}
                data-testid="organization-roles-create-name"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  loading={createM.isPending}
                  data-testid="organization-roles-create"
                >
                  <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  {t('organization.roles.create')}
                </Button>
              </div>
              {createError !== null ? (
                <p
                  role="alert"
                  data-testid="organization-roles-create-error"
                  className="m-0 text-caption text-bad"
                >
                  {createError}
                </p>
              ) : null}
            </form>
          ) : null}
        </Panel>

        <Panel
          caption={t('organization.roles.checklist.title')}
          testId="organization-roles-checklist"
          actions={
            selected !== null && !selected.builtin && canEditCustom ? (
              <Button
                variant="primary"
                size="sm"
                loading={updateM.isPending}
                onClick={() => {
                  void handleSavePermissions();
                }}
                data-testid="organization-roles-save"
              >
                {t('organization.roles.save_permissions')}
              </Button>
            ) : undefined
          }
        >
          {selected === null ? (
            <p className="px-4 py-6 text-body text-ink-low">
              {t('organization.roles.empty_custom')}
            </p>
          ) : (
            <div className="flex flex-col gap-4 px-4 py-4">
              {selected.builtin ? (
                <p className="m-0 text-caption text-ink-low">{t('organization.roles.readonly')}</p>
              ) : null}
              {ORG_ROLE_PERMISSION_GROUPS.map((group) => (
                <fieldset key={group.id} className="m-0 min-w-0 border-0 p-0">
                  <legend className="mb-2 text-label font-medium text-ink">
                    {t(group.labelKey)}
                  </legend>
                  <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {group.permissions.map((item) => {
                      const checked = draftPermissions.includes(item.id);
                      return (
                        <li key={item.id}>
                          <label className="flex min-w-0 items-center gap-3 rounded-md px-2 py-1.5 hover:bg-active/40">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={checklistReadOnly || mutationPending}
                              onChange={() => togglePermission(item.id)}
                              data-testid={`organization-roles-perm-${item.id}`}
                              className="h-4 w-4 rounded border-line accent-forest disabled:opacity-40"
                            />
                            <span className="min-w-0 text-body text-ink">{t(item.labelKey)}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              ))}
              {saveError !== null ? (
                <p
                  role="alert"
                  data-testid="organization-roles-save-error"
                  className="m-0 text-caption text-bad"
                >
                  {saveError}
                </p>
              ) : null}
            </div>
          )}
        </Panel>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteM.isPending) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        title={t('organization.roles.delete_title')}
        body={
          <div className="flex flex-col gap-2">
            <p className="m-0">
              {t('organization.roles.delete_body', { name: deleteTarget?.name ?? '' })}
            </p>
            {deleteError !== null ? (
              <p
                role="alert"
                data-testid="organization-roles-delete-error"
                className="m-0 text-caption text-bad"
              >
                {deleteError}
              </p>
            ) : null}
          </div>
        }
        confirmLabel={t('organization.roles.delete_confirm')}
        cancelLabel={t('organization.action.cancel')}
        closeLabel={t('organization.roles.delete_close')}
        tone="danger"
        loading={deleteM.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
        testId="organization-roles-delete-dialog"
      />
    </div>
  );
}

function customPermissionsOf(
  permissions: readonly TenantOrgPermission[],
): readonly CustomOrgRolePermission[] {
  return permissions.filter(isCustomOrgRolePermission);
}
